import React, { memo, useId } from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

/**
 * Props for the EnhancedQuickAccessCard component
 */
export interface EnhancedQuickAccessCardProps {
  /** Title of the card */
  title: string;
  /** Description text */
  description?: string;
  /** Icon element to display */
  icon: React.ReactNode;
  /** Navigation path */
  path: string;
  /** Color for the icon background and accents */
  color: string;
  /** Optional statistics to display */
  stats?: Array<{
    label: string;
    value: string | number;
  }>;
  /** Activity indicator status */
  activityIndicator?: "high" | "medium" | "low" | "none";
  /** Last activity timestamp or description */
  lastActivity?: string;
}

/**
 * StyledCard component using MUI's styled() with hover animations
 * Features:
 * - Smooth 0.2s transitions on transform and box-shadow
 * - translateY(-4px) on hover for lift effect
 * - Enhanced box-shadow on hover for depth
 * - 8px border radius for consistency
 */
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "8px",
  transition: "transform 0.2s, box-shadow 0.2s",
  height: "100%",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6],
  },
}));

/**
 * Activity indicator color mapping
 */
const activityIndicatorColors = {
  high: "#4CAF50",    // Green
  medium: "#FF9800",  // Orange
  low: "#F44336",     // Red
  none: "transparent",
};

/**
 * EnhancedQuickAccessCard Component
 * 
 * An enhanced version of the QuickAccessCard that includes support for:
 * - Contextual statistics display
 * - Activity status indicators
 * - Last activity information
 * - Smooth hover animations with styled components
 * 
 * @param props - EnhancedQuickAccessCardProps
 * @returns React component displaying the enhanced quick access card
 */
const EnhancedQuickAccessCardComponent: React.FC<EnhancedQuickAccessCardProps> = ({
  title,
  description,
  icon,
  path,
  color,
  stats,
  activityIndicator,
  lastActivity,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const titleId = useId();
  const descriptionId = useId();
  const activityId = useId();
  const statsId = useId();

  // Handle keyboard events for Enter and Space keys
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(path);
    }
  };

  const iconBackground = alpha(
    color,
    theme.palette.mode === "dark" ? 0.24 : 0.12
  );
  const effectiveTextColor = theme.palette.getContrastText(
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : theme.palette.background.default
  );

  return (
    <article
      role="group"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <StyledCard
        elevation={2}
        sx={{
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardActionArea
          sx={{
            height: "100%",
            p: 2,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            "&.Mui-focusVisible": {
              outline: `3px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
          onClick={() => navigate(path)}
          onKeyPress={handleKeyPress}
          aria-label={`Navigate to ${title}`}
          aria-describedby={`${descriptionId}${activityIndicator && activityIndicator !== "none" ? ` ${activityId}` : ""}`}
          role="button"
          tabIndex={0}
        >
          <CardContent sx={{ width: "100%", p: 0 }}>
            {/* Header with icon and title */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: iconBackground,
                  color,
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
              <Typography
                id={titleId}
                variant="h6"
                component="div"
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 500,
                  color: effectiveTextColor,
                  flex: 1,
                }}
              >
                {title}
              </Typography>

              {/* Activity indicator dot */}
              {activityIndicator && activityIndicator !== "none" && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor:
                      activityIndicatorColors[
                        activityIndicator as keyof typeof activityIndicatorColors
                      ],
                    flexShrink: 0,
                    ml: 1,
                  }}
                  role="img"
                  id={activityId}
                  aria-label={`Activity level: ${activityIndicator}`}
                />
              )}
            </Box>

            {/* Description text */}
            {description && (
              <Typography
                id={descriptionId}
                variant="body2"
                color="text.secondary"
                sx={{ mb: stats || lastActivity ? 2 : 0 }}
              >
                {description}
              </Typography>
            )}

            {/* Statistics section */}
            {stats && stats.length > 0 && (
              <Box 
                sx={{ mb: lastActivity ? 1 : 0 }}
                role="region"
                aria-labelledby={titleId}
                aria-label={`${title} statistics`}
              >
                {stats.map((stat, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: index < stats.length - 1 ? 1 : 0,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {stat.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Last activity text */}
            {lastActivity && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: stats ? 1 : 0,
                }}
              >
                Last activity: {lastActivity}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </StyledCard>
    </article>
  );
};

export const EnhancedQuickAccessCard = memo(
  EnhancedQuickAccessCardComponent
);
export default EnhancedQuickAccessCard;
